import { ssoSchema } from "@frontend-shared/schema/auth";
import type { ISamlConfig } from "@frontend-shared/types";
import { resolveSsoErrorKey } from "@frontend-shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRightCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useSaml } from "@/api/auth/useSaml";
import { useSingleOrganizationSaml } from "@/api/auth/useSingleOrganizationSaml";
import { FormSubmitButton } from "@/components/FormSubmitButton";
import { Organization } from "@/components/icons";
import { SpinningLoader } from "@/components/SpinningLoader";
import { StyledInput } from "@/components/StyledInput";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PATHS } from "@/constants/paths";
import { assignSafeLocation } from "@/lib/safe-redirect";
import { clearLocalStorage, cn } from "@/lib/utils";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

import { AuthHeader } from "./AuthHeader";
import { AuthLayout } from "./AuthLayout";
import { AUTH_CONTROL_HEIGHT_CLASS } from "./constants/auth-controls";
import {
  markSsoAutoRedirectAttempted,
  wasSsoAutoRedirectJustAttempted,
} from "./utils";

const Sso = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");

  const [searchParams, setSearchParams] = useSearchParams();

  const { mutate: samlMutation, isPending } = useSaml();
  const {
    data: singleOrgSaml,
    isLoading: isCheckingSingleOrg,
    isError: isSingleOrgError,
  } = useSingleOrganizationSaml();

  const [samlConfigs, setSamlConfigs] = useState<ISamlConfig[]>([]);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // NOTE: NSAI-6319 — read from the params on the first render only, and kept
  // in state from then on, because the effect that shows the toast below also
  // strips `error` from the URL. Reading the params again later would see a
  // clean URL and conclude the page was opened normally — which is precisely
  // how the auto-redirect used to bounce a rejected login straight back to the
  // IdP.
  const [hasSsoCallbackError] = useState<boolean>(() =>
    Boolean(searchParams.get("error")),
  );

  const emailForm = useForm({
    ...FORM_VALIDATION_OPTIONS,
    defaultValues: { email: "" },
    resolver: zodResolver(ssoSchema(t)),
  });

  const handleSubmit = emailForm.handleSubmit(async (values) => {
    samlMutation(values.email, {
      onSuccess: (data) => {
        if (data.length === 0) {
          toast.warning(t("sso.saml_config_not_found"));
        } else if (data.length === 1) {
          try {
            setIsRedirecting(true);
            assignSafeLocation(data[0].samlUrl);
          } catch {
            setIsRedirecting(false);
            toast.error(t("sso.saml_config_not_found"));
          }
        } else {
          setSamlConfigs(data);
        }
      },
    });
  });

  useEffect(() => {
    clearLocalStorage();
  }, []);

  // NOTE: NSAI-6112 — `error` is a code the SAML callback redirect sets, but a
  // crafted link can carry any text, so it is resolved against an allowlist and
  // an unknown value shows the generic SSO failure instead of itself.
  useEffect(() => {
    const error = searchParams.get("error");
    if (error) {
      toast.error(t(resolveSsoErrorKey({ code: error })));
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, t]);

  // NOTE: Auto-redirect to single organization SSO if available
  useEffect(() => {
    if (isCheckingSingleOrg || isSingleOrgError || !singleOrgSaml) {
      return;
    }

    // NOTE: NSAI-6319 — the two conditions under which this deployment's own
    // login flow must NOT run. A rejected assertion comes back as
    // `/auth/sso?error=…`, and redirecting to the IdP again produces the same
    // rejection: that is a loop the user cannot escape and cannot read, and it
    // used to run until the `/auth/*` rate limiter answered 429 — a budget
    // shared with every other login on the deployment. The cooldown covers the
    // laps that arrive without an `error` to go on.
    if (hasSsoCallbackError || wasSsoAutoRedirectJustAttempted()) {
      return;
    }

    try {
      markSsoAutoRedirectAttempted();
      setIsRedirecting(true);
      assignSafeLocation(singleOrgSaml.samlUrl);
    } catch {
      setIsRedirecting(false);
      toast.error(t("sso.saml_config_not_found"));
    }
  }, [
    singleOrgSaml,
    isCheckingSingleOrg,
    isSingleOrgError,
    hasSsoCallbackError,
    t,
  ]);

  const handleBackButtonClick = useCallback(() => {
    if (samlConfigs.length) {
      setSamlConfigs([]);
    } else {
      emailForm.reset();
      navigate(PATHS.AUTH.SIGN_IN);
    }
  }, [samlConfigs.length, emailForm, navigate]);

  // NOTE: Show loader while checking for single organization SSO
  if (isCheckingSingleOrg || isRedirecting) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-4 items-center justify-center min-h-[300px]">
          <SpinningLoader />
          <p className="text-muted-foreground">
            {t("sso.checking_organization")}
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="flex flex-col gap-4">
        <AuthHeader description={t("sign_in.description")} className="mb-2" />
        {samlConfigs.length ? (
          <>
            {samlConfigs.map((config) => (
              <Button
                key={config.organizationUuid}
                variant="outline"
                size="lg"
                className="bg-background hover:bg-accent/30 hover:border-primary hover:text-foreground justify-between"
                data-testid={`sso-continue-with-button-${config.organizationUuid}`}
                onClick={() => {
                  try {
                    assignSafeLocation(config.samlUrl);
                  } catch {
                    toast.error(t("sso.saml_config_not_found"));
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  <Organization className="size-8" />

                  {t("sso.continue_with", {
                    organizationName: config.organizationName,
                  })}
                </div>

                <ArrowRightCircle className="w-4 h-4" />
              </Button>
            ))}
          </>
        ) : (
          <Form {...emailForm}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField
                control={emailForm.control}
                name="email"
                render={({ field, fieldState: { error } }) => (
                  <FormItem>
                    <FormLabel>
                      {t("sso.email_or_domain.label", {
                        defaultValue: t("email.label"),
                      })}
                    </FormLabel>
                    <FormControl>
                      <StyledInput
                        className={AUTH_CONTROL_HEIGHT_CLASS}
                        placeholder={t("sso.email_or_domain.placeholder", {
                          defaultValue: t("email.placeholder"),
                        })}
                        {...field}
                        isError={!!error}
                        data-testid="sso-email-input"
                      />
                    </FormControl>
                    <FormMessage data-testid="sso-email-error" />
                  </FormItem>
                )}
              />

              <FormSubmitButton
                className={cn("w-full", AUTH_CONTROL_HEIGHT_CLASS)}
                data-testid="sso-submit-button"
                isLoading={isPending}
              >
                {t("sso.continue")}
              </FormSubmitButton>
              {/* NOTE: NSAI-7736 — a member identified by a login name has no
                  email to type here; the domain or their organization's own
                  sign-in link (/auth/sso/<uuid>) gets them to their IdP. */}
              <p
                className="text-sm text-muted-foreground text-center"
                data-testid="sso-no-email-hint"
              >
                {t("sso.no_email_hint")}
              </p>
            </form>
          </Form>
        )}
        <Button
          variant={samlConfigs.length ? "link" : "outline"}
          size="lg"
          className="bg-background hover:bg-accent/30 hover:border-primary hover:text-foreground"
          data-testid="sso-back-button"
          onClick={handleBackButtonClick}
        >
          {t("sso.back")}
        </Button>
      </div>
    </AuthLayout>
  );
};

export default Sso;
